/**
 * @file routes/_app/certificates/index.tsx
 * @description 驗證證書列表頁（M3）。
 *
 * 視覺來源：pages/_dashboard/certificates/list/page.tsx
 * API：GET /api/certificates
 *
 * 功能：
 * - meta 行：有效 / 30 日內到期 / 已暫停
 * - 表格：編號(mono)、客戶名稱、方案(StatusPill)、發證日、到期日、狀態(StatusPill)、操作
 * - 操作：詳情 + 更多（下載PDF / 暫停 / 撤銷 / 刪除）
 * - 依方案分布 mini-bar（頁底裝飾）
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { message } from '@/lib/antd-app';
import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Popconfirm,
  Row,
  Col,
  Dropdown,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import {
  Plus,
  Search,
  Download,
  Edit,
  Trash2,
  Eye,
  Award,
  MoreHorizontal,
  PauseCircle,
  Ban,
  CheckCircle2,
  AlarmClock,
  CalendarDays,
} from 'lucide-react';
import dayjs from 'dayjs';
import { apiClient } from '@/lib/api-client';
import { ListPageShell, PageMetaItem, StatusPill, EmptyState } from '@/components/shell';
import { color, font, leading, radius, space, text, tracking, weight } from '@/styles/tokens';
import ClientSelect from '@/components/common/ClientSelect';
import { hideBelow } from '@/components/common/responsive-columns';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/certificates/')({ component: CertificatesListPage });

/* ============================================================
 * 型別定義
 * ============================================================ */

interface Certificate {
  id: number;
  certNumber: string;
  /** 客戶 ID（後端主鍵，用於回填 ClientSelect） */
  clientId?: number;
  clientName: string;
  scheme: string;
  issueDate: string;
  expiryDate: string;
  status: '有效' | '即將到期' | '已過期' | '已暫停' | '已撤銷';
  summary: string;
}

/** 後端 GET /api/certificates 回傳的列表項 */
interface ApiCertificateItem {
  id: number;
  certNo: string;
  /** 證書字號（序92；舊資料 null 回落 certNo） */
  certDocNo?: string | null;
  clientId: number;
  clientName: string;
  caseId: number;
  status: string;
  issuedAt: string | null;
  expiresAt: string | null;
  currentVersionId: number | null;
  createdAt: string;
}

interface ApiCertificateList {
  items: ApiCertificateItem[];
  total: number;
}

function mapApiToCertificate(item: ApiCertificateItem): Certificate {
  const statusMap: Record<string, Certificate['status']> = {
    製證中: '有效',
    待寄送: '有效',
    已寄送: '有效',
    有效: '有效',
    即將到期: '即將到期',
    已過期: '已過期',
    已暫停: '已暫停',
    已撤銷: '已撤銷',
  };
  return {
    id: item.id,
    certNumber: item.certDocNo ?? item.certNo,
    clientId: item.clientId,
    clientName: item.clientName,
    scheme: '有機',
    issueDate: item.issuedAt ? item.issuedAt.slice(0, 10) : '—',
    expiryDate: item.expiresAt ? item.expiresAt.slice(0, 10) : '—',
    status: statusMap[item.status] ?? '有效',
    summary: '',
  };
}

/* ============================================================
 * 常量
 * ============================================================ */

const schemeOptions = [
  { label: '有機', value: '有機' },
  { label: '產銷履歷(TAP)', value: '產銷履歷(TAP)' },
  { label: '友善', value: '友善' },
];

const statusOptions = [
  { label: '有效', value: '有效' },
  { label: '即將到期', value: '即將到期' },
  { label: '已過期', value: '已過期' },
  { label: '已暫停', value: '已暫停' },
  { label: '已撤銷', value: '已撤銷' },
];

const STATUS_TONE_MAP: Record<
  Certificate['status'],
  { tone: 'success' | 'warning' | 'muted' | 'info' | 'danger'; dot: boolean }
> = {
  有效: { tone: 'success', dot: true },
  即將到期: { tone: 'warning', dot: true },
  已過期: { tone: 'muted', dot: false },
  已暫停: { tone: 'info', dot: true },
  已撤銷: { tone: 'danger', dot: false },
};

const SCHEME_COLOR_MAP: Record<string, string> = {
  有機: color.emerald[600],
  '產銷履歷(TAP)': color.indigo[600],
  友善: color.sky[600],
};

/* ============================================================
 * 依方案分布 mini-bar
 * ============================================================ */

function SchemeDistributionBanner({ certs }: { certs: Certificate[] }) {
  const total = certs.length || 1;
  const groups = useMemo(() => {
    const map = new Map<string, number>();
    certs.forEach((c) => map.set(c.scheme, (map.get(c.scheme) ?? 0) + 1));
    return Array.from(map.entries())
      .map(([label, count]) => ({
        label,
        count,
        ratio: count / total,
        color: SCHEME_COLOR_MAP[label] ?? color.text.muted,
      }))
      .sort((a, b) => b.count - a.count);
  }, [certs, total]);

  return (
    <div
      style={{
        background: color.surface.base,
        border: `1px solid ${color.border.subtle}`,
        borderRadius: radius.md,
        padding: `${space[3]}px ${space[5]}px`,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span
          style={{
            fontSize: text.xs,
            fontWeight: weight.semibold,
            color: color.text.muted,
            letterSpacing: tracking.widest,
            textTransform: 'uppercase',
          }}
        >
          依方案分布
        </span>
        <span style={{ fontSize: text.sm, color: color.text.tertiary }}>
          總計 <strong style={{ color: color.text.primary }}>{certs.length}</strong> 張
        </span>
      </div>
      {groups.map((g) => (
        <div
          key={g.label}
          style={{
            display: 'grid',
            gridTemplateColumns: '110px 1fr 64px',
            alignItems: 'center',
            gap: space[3],
          }}
        >
          <span
            style={{
              fontSize: text.sm,
              color: color.text.secondary,
              fontWeight: weight.medium,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {g.label}
          </span>
          <div
            style={{
              height: 6,
              background: color.surface.sunken,
              borderRadius: radius.full,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${g.ratio * 100}%`,
                background: g.color,
                borderRadius: radius.full,
                transition: 'width 480ms cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            />
          </div>
          <span
            style={{
              fontFamily: font.mono,
              fontSize: text.sm,
              color: color.text.primary,
              fontWeight: weight.semibold,
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {g.count}
            <span style={{ color: color.text.muted, marginLeft: 4, fontWeight: weight.regular }}>
              ({(g.ratio * 100).toFixed(0)}%)
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

/* ============================================================
 * 詳情 Modal 小型欄位
 * ============================================================ */

function DetailField({
  label,
  value,
  icon,
  span,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  span?: 1 | 2;
}) {
  return (
    <div style={{ gridColumn: span === 2 ? 'span 2' : undefined, minWidth: 0 }}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: text.xs,
          color: color.text.muted,
          textTransform: 'uppercase',
          letterSpacing: tracking.widest,
          fontWeight: weight.semibold,
          marginBottom: 4,
        }}
      >
        {icon}
        {label}
      </div>
      <div
        style={{
          fontSize: text.md,
          color: color.text.primary,
          lineHeight: leading.snug,
          wordBreak: 'break-word',
        }}
      >
        {value || <span style={{ color: color.text.muted }}>—</span>}
      </div>
    </div>
  );
}

/* ============================================================
 * 主頁面
 * ============================================================ */

function CertificatesListPage() {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingCert, setEditingCert] = useState<Certificate | null>(null);
  const [viewingCert, setViewingCert] = useState<Certificate | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [filterScheme, setFilterScheme] = useState<string | undefined>(undefined);
  const [form] = Form.useForm();

  const fetchCertificates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (filterStatus) params.set('status', filterStatus);
      if (searchText.trim()) params.set('q', searchText.trim());
      const data = await apiClient.get<ApiCertificateList>(
        `/api/certificates?${params.toString()}`,
      );
      setCertificates(data.items.map(mapApiToCertificate));
      setTotal(data.total);
    } catch (err) {
      console.error('[certificates-list] 拉取證書列表失敗:', err);
      message.error('無法載入證書列表，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, searchText]);

  useEffect(() => {
    void fetchCertificates();
  }, [fetchCertificates]);

  const filteredCertificates = useMemo(() => {
    let result = certificates;
    if (searchText.trim()) {
      const keyword = searchText.trim().toLowerCase();
      result = result.filter(
        (c) =>
          c.certNumber.toLowerCase().includes(keyword) ||
          c.clientName.toLowerCase().includes(keyword),
      );
    }
    if (filterStatus) result = result.filter((c) => c.status === filterStatus);
    if (filterScheme) result = result.filter((c) => c.scheme === filterScheme);
    return result;
  }, [certificates, searchText, filterStatus, filterScheme]);

  const stats = useMemo(
    () => ({
      valid: certificates.filter((c) => c.status === '有效').length,
      expiring: certificates.filter((c) => c.status === '即將到期').length,
      suspended: certificates.filter((c) => c.status === '已暫停').length,
    }),
    [certificates],
  );

  const handleAdd = () => {
    setEditingCert(null);
    form.resetFields();
    setModalOpen(true);
  };
  const handleEdit = (record: Certificate) => {
    setEditingCert(record);
    form.setFieldsValue({
      ...record,
      issueDate: dayjs(record.issueDate),
      expiryDate: dayjs(record.expiryDate),
    });
    setModalOpen(true);
  };
  const handleView = (record: Certificate) => {
    setViewingCert(record);
    setViewModalOpen(true);
  };
  const handleDelete = () => {
    message.info('請透過後端管理介面刪除證書（此功能尚未開放前端操作）');
  };
  const handleChangeStatus = (_id: number, newStatus: Certificate['status']) => {
    message.info(`「${newStatus}」操作請透過後端管理流程執行`);
  };
  const handleDownload = (record: Certificate) => {
    window.open(`/api/certificates/${record.id}/print.pdf`, '_blank');
  };
  const handleSubmit = async () => {
    try {
      await form.validateFields();
      message.info('證書核發請透過驗證決定通過後由系統自動觸發');
      setModalOpen(false);
      form.resetFields();
    } catch (error) {
      console.error('證書表單驗證失敗:', error);
    }
  };
  const handleExport = () => {
    message.success('證書資料匯出成功（模擬）');
  };
  const handleReset = () => {
    setSearchText('');
    setFilterStatus(undefined);
    setFilterScheme(undefined);
  };

  const columns: ColumnsType<Certificate> = [
    {
      title: '證書編號',
      dataIndex: 'certNumber',
      key: 'certNumber',
      width: 200,
      sorter: (a, b) => a.certNumber.localeCompare(b.certNumber),
      render: (val: string, record) => (
        <Button
          type="link"
          size="small"
          style={{ fontFamily: font.mono, fontSize: text.sm, padding: 0 }}
          onClick={() =>
            void navigate({ to: '/certificates/$id', params: { id: String(record.id) } })
          }
        >
          {val}
        </Button>
      ),
    },
    {
      title: '客戶名稱',
      dataIndex: 'clientName',
      key: 'clientName',
      width: 200,
      ellipsis: true,
      render: (val: string) => (
        <span style={{ fontWeight: weight.medium, color: color.text.primary }}>{val}</span>
      ),
    },
    {
      ...hideBelow('lg'),
      title: '驗證方案',
      dataIndex: 'scheme',
      key: 'scheme',
      width: 140,
      render: (scheme: string) => (
        <StatusPill tone="brand" size="sm" variant="outline">
          {scheme}
        </StatusPill>
      ),
    },
    {
      ...hideBelow('lg'),
      title: '發證日期',
      dataIndex: 'issueDate',
      key: 'issueDate',
      width: 120,
      sorter: (a, b) => a.issueDate.localeCompare(b.issueDate),
      render: (val: string) => (
        <span style={{ color: color.text.secondary, fontSize: text.sm }}>{val}</span>
      ),
    },
    {
      title: '到期日期',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 120,
      sorter: (a, b) => a.expiryDate.localeCompare(b.expiryDate),
      render: (val: string) => (
        <span style={{ color: color.text.secondary, fontSize: text.sm }}>{val}</span>
      ),
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: Certificate['status']) => {
        const cfg = STATUS_TONE_MAP[status];
        return (
          <StatusPill tone={cfg.tone} dot={cfg.dot} size="sm">
            {status}
          </StatusPill>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 140,
      fixed: 'right',
      render: (_, record) => {
        const moreItems: MenuProps['items'] = [
          {
            key: 'download',
            icon: <Download size={13} />,
            label: '下載 PDF',
            onClick: () => handleDownload(record),
          },
          {
            key: 'edit',
            icon: <Edit size={13} />,
            label: '編輯',
            onClick: () => handleEdit(record),
          },
          { type: 'divider' },
          ...(record.status !== '已暫停' && record.status !== '已撤銷'
            ? [
                {
                  key: 'suspend',
                  icon: <PauseCircle size={13} />,
                  label: '暫停',
                  onClick: () => handleChangeStatus(record.id, '已暫停'),
                },
              ]
            : []),
          ...(record.status !== '已撤銷'
            ? [
                {
                  key: 'revoke',
                  icon: <Ban size={13} />,
                  label: '撤銷',
                  danger: true,
                  onClick: () => handleChangeStatus(record.id, '已撤銷'),
                },
              ]
            : []),
          { type: 'divider' },
          {
            key: 'delete',
            icon: <Trash2 size={13} />,
            label: (
              <Popconfirm
                title="確認刪除"
                description={`確定要刪除「${record.certNumber}」嗎？`}
                onConfirm={() => handleDelete()}
                okText="確定"
                cancelText="取消"
                okButtonProps={{ danger: true }}
              >
                <span>刪除</span>
              </Popconfirm>
            ),
            danger: true,
          },
        ];
        return (
          <div style={{ display: 'flex', gap: 4 }}>
            <Tooltip title="檢視詳情">
              <Button
                type="text"
                size="small"
                icon={<Eye size={14} />}
                onClick={() => handleView(record)}
              />
            </Tooltip>
            <Dropdown menu={{ items: moreItems }} trigger={['click']} placement="bottomRight">
              <Button type="text" size="small" icon={<MoreHorizontal size={14} />} />
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <ListPageShell
      title="證書列表"
      description="管理所有已發出的驗證證書，包含發證、續證、暫停、撤銷的全生命周期。"
      meta={
        <>
          <PageMetaItem
            icon={<CheckCircle2 size={13} />}
            label="有效證書"
            value={`${stats.valid} 張`}
          />
          <PageMetaItem
            icon={<AlarmClock size={13} />}
            label="30 日內到期"
            value={`${stats.expiring} 張`}
          />
          <PageMetaItem
            icon={<PauseCircle size={13} />}
            label="已暫停"
            value={`${stats.suspended} 張`}
          />
        </>
      }
      actions={
        <>
          <Button icon={<Download size={14} />} onClick={handleExport}>
            匯出
          </Button>
          <Button type="primary" icon={<Plus size={14} />} onClick={handleAdd}>
            新增證書
          </Button>
        </>
      }
      filters={
        <Row gutter={[12, 8]} style={{ width: '100%' }} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="搜尋證書編號或客戶名稱"
              prefix={<Search size={14} color={color.text.muted} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="狀態"
              options={statusOptions}
              value={filterStatus}
              onChange={setFilterStatus}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="方案"
              options={schemeOptions}
              value={filterScheme}
              onChange={setFilterScheme}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={4}>
            <Button
              type="text"
              onClick={handleReset}
              disabled={!searchText && !filterStatus && !filterScheme}
            >
              重置
            </Button>
          </Col>
        </Row>
      }
      toolbar={
        <>
          <span style={{ fontSize: text.sm, color: color.text.tertiary }}>
            本頁 <strong style={{ color: color.text.primary }}>{certificates.length}</strong> 筆 /
            共 <strong style={{ color: color.text.primary }}>{total}</strong> 筆
          </span>
        </>
      }
    >
      {!loading && certificates.length === 0 ? (
        <EmptyState
          variant="empty"
          title="尚無符合條件的證書"
          description="試著調整搜尋關鍵字或篩選條件，或建立第一張證書。"
          action={
            <Button type="primary" icon={<Plus size={14} />} onClick={handleAdd}>
              新增證書
            </Button>
          }
          size="md"
        />
      ) : (
        <Table<Certificate>
          columns={columns}
          dataSource={filteredCertificates}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 筆資料`,
            onChange: (p) => setPage(p),
          }}
          size="middle"
        />
      )}

      {/* 依方案分布 banner */}
      <div style={{ padding: space[5], borderTop: `1px solid ${color.border.subtle}` }}>
        <SchemeDistributionBanner certs={certificates} />
      </div>

      {/* 新增/編輯 Modal */}
      <Modal
        title={editingCert ? '編輯證書' : '新增證書'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        okText="儲存"
        cancelText="取消"
        width={680}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="客戶名稱"
                name="clientId"
                rules={[{ required: true, message: '請選擇客戶' }]}
              >
                <ClientSelect nameField="clientName" />
              </Form.Item>
              {/* 隱藏字段：保存 clientName 字串供列表顯示 */}
              <Form.Item name="clientName" hidden>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="驗證方案"
                name="scheme"
                rules={[{ required: true, message: '請選擇驗證方案' }]}
              >
                <Select placeholder="請選擇驗證方案" options={schemeOptions} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="發證日期"
                name="issueDate"
                rules={[{ required: true, message: '請選擇發證日期' }]}
              >
                <DatePicker style={{ width: '100%' }} placeholder="請選擇發證日期" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="到期日期"
                name="expiryDate"
                rules={[{ required: true, message: '請選擇到期日期' }]}
              >
                <DatePicker style={{ width: '100%' }} placeholder="請選擇到期日期" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="狀態" name="status" rules={[{ required: true, message: '請選擇狀態' }]}>
            <Select placeholder="請選擇狀態" options={statusOptions} />
          </Form.Item>
          <Form.Item label="證書內容摘要" name="summary">
            <Input.TextArea rows={3} placeholder="請輸入證書內容摘要" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 證書詳情 Modal */}
      <Modal
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Award size={16} color={color.brand.primary} />
            <span>證書詳情</span>
          </span>
        }
        open={viewModalOpen}
        onCancel={() => setViewModalOpen(false)}
        footer={[
          <Button
            key="download"
            icon={<Download size={14} />}
            onClick={() => viewingCert && handleDownload(viewingCert)}
          >
            下載 PDF
          </Button>,
          <Button key="close" type="primary" onClick={() => setViewModalOpen(false)}>
            關閉
          </Button>,
        ]}
        width={620}
      >
        {viewingCert && (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: space[4], marginTop: space[4] }}
          >
            {/* 編號 + 狀態行 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: `${space[3]}px ${space[4]}px`,
                background: color.surface.muted,
                borderRadius: radius.md,
                border: `1px solid ${color.border.subtle}`,
              }}
            >
              <span
                style={{
                  fontFamily: font.mono,
                  fontSize: text.md,
                  fontWeight: weight.semibold,
                  color: color.text.primary,
                }}
              >
                {viewingCert.certNumber}
              </span>
              <StatusPill
                tone={STATUS_TONE_MAP[viewingCert.status].tone}
                dot={STATUS_TONE_MAP[viewingCert.status].dot}
                size="md"
              >
                {viewingCert.status}
              </StatusPill>
            </div>

            {/* 屬性網格 */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                rowGap: space[4],
                columnGap: space[6],
              }}
            >
              <DetailField label="客戶名稱" value={viewingCert.clientName} />
              <DetailField
                label="驗證方案"
                value={
                  <StatusPill tone="brand" size="sm" variant="outline">
                    {viewingCert.scheme}
                  </StatusPill>
                }
              />
              <DetailField
                label="發證日期"
                value={viewingCert.issueDate}
                icon={<CalendarDays size={12} />}
              />
              <DetailField
                label="到期日期"
                value={viewingCert.expiryDate}
                icon={<AlarmClock size={12} />}
              />
              <DetailField label="證書內容摘要" value={viewingCert.summary} span={2} />
            </div>
          </div>
        )}
      </Modal>
    </ListPageShell>
  );
}
